/**
 * Centralized Energy Rate Constants
 *
 * These are system defaults. Rates are now configurable per company/group/unit
 * via the energy_rates table and rate assignment system.
 */

export const ENERGY_RATES = {
  /** Grid sell/export rate - $/kWh for energy sold back to utility (NEM 3.0 default) */
  GRID_SELL_RATE_PER_KWH: 0.08,

  /** Grid import rate - $/kWh for energy purchased from utility */
  GRID_IMPORT_RATE_PER_KWH: 0.18,

  /** Diesel cost per gallon */
  DIESEL_COST_PER_GALLON: 4.5,
} as const;

export const ENERGY_CONVERSION = {
  /** Effective kWh output per gallon of diesel (accounts for generator efficiency ~26%) */
  DIESEL_EFFECTIVE_KWH_PER_GALLON: 10.0,

  /** kg CO2 sequestered per urban tree per year (EPA equivalency estimate) */
  KG_CO2_PER_TREE_PER_YEAR: 22,

  /** Theoretical energy content of diesel (not used for calculations, reference only) */
  DIESEL_ENERGY_CONTENT_KWH_PER_GALLON: 38.6,

  /** CO2 emissions per gallon of diesel burned (kg) */
  CO2_KG_PER_DIESEL_GALLON: 10.18,

  /** CO2 emissions per kWh from grid (US average, kg) */
  CO2_KG_PER_GRID_KWH: 0.42,

  /** Kilograms per metric ton */
  METRIC_TON_KG: 1000,
} as const;

/**
 * Combined constants for backwards compatibility
 * @deprecated Use ENERGY_RATES or ENERGY_CONVERSION instead
 */
export const ENERGY_CONSTANTS = {
  ...ENERGY_RATES,
  ...ENERGY_CONVERSION,
  // Legacy aliases
  ELECTRICITY_RATE_PER_KWH: ENERGY_RATES.GRID_IMPORT_RATE_PER_KWH,
  DIESEL_KWH_PER_GALLON: ENERGY_CONVERSION.DIESEL_EFFECTIVE_KWH_PER_GALLON,
  COST_PER_KWH_SAVED: 0.15, // DEPRECATED: Use ENERGY_RATES.GRID_IMPORT_RATE_PER_KWH (0.18) instead
} as const;

export type EnergyRates = typeof ENERGY_RATES;
export type EnergyConversion = typeof ENERGY_CONVERSION;
